import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/lib/auth/AuthContext";
import { apiRequest, setInstanceGetter } from "@/lib/api";
import { beginUserAction } from "@/lib/correlation";
import {
  forgetBrowserInstance,
  forgetTabInstance,
  keepInstanceHere,
  readBrowserInstance,
  readTabInstance,
} from "@/lib/instanceChoice";
import type { MeInstance, MeResponse } from "@shared/types";
import { ME_QUERY_KEY, useMe } from "@/hooks/use-me";
import { instancePath, isInstanceIdShape, splitInstancePath } from "@/lib/instancePath";
import { isGlobalPath, landingAfterInstanceSwitch } from "@/lib/routes";
import { logger } from "@/lib/logger";

/**
 * Which Instance the user is working in (FR-20; FR-22 as round 9 T-58 changed it, owner
 * 2026-10-06).
 *
 * **The address names the instance.** Every instance-scoped screen is under `/i/<instance id>/`
 * (`lib/instancePath.ts`), and inside it the selection is DERIVED from the address: a deep link
 * opens its instance, a reload stays, and each tab follows its own address. The id in the address
 * is never trusted for data. It is selected only while `GET /me` lists it (an id it does not list
 * is asked once more, `/me` refetched, so an instance created a moment ago opens), and the server
 * re-checks the `x-instance-id` header on every request. An id the person cannot open (malformed,
 * unknown, disabled or forbidden, all alike) selects nothing: `addressInstance.status` is
 * `refused`, the shell shows one message for all four, and no instance-scoped request is made.
 *
 * Every tenant-scoped request carries the selection in the `x-instance-id` header, and the server
 * reads it only from there, so a header the token does not entitle is rejected as a 401 rather
 * than quietly serving another tenant's rows.
 *
 * **Outside an instance's address** (the picker, a platform or personal screen, an address from
 * before T-58) the selection is the instance this tab was last in. The order (round 6 T-08, owner
 * 2026-10-01: "The user's last instance should be saved and the user should login to that
 * instance. So the user with multiple tenant and instance doesn't always login to a default by
 * default."):
 * 1. this tab's own choice (`sessionStorage`, `lib/instanceChoice.ts`), which follows the address
 *    while the tab is in an instance, so two tabs in two instances never move each other;
 * 2. the only instance, when there is exactly one and it is active (#419);
 * 3. the person's saved instance, `GET /me` `preferences.lastInstanceId` (the server names it only
 *    while the person can still reach it), as it was when this page signed in, so another tab's
 *    later switch does not move this one;
 * 4. this browser's last instance (`localStorage`), only when the server holds none;
 * 5. nothing: `/` and `/overview` then land on the first active readable instance (`OverviewLanding`),
 *    every other route goes to the picker.
 * An instance-scoped address without `/i/` (a bookmark, a notification sent before T-58) is sent
 * to the same screen in that instance (`App.tsx`). Signing out forgets the tab's and the browser's
 * choice (`AuthContext`).
 *
 * **What is saved.** Whatever instance this page ends up in is sent to `PUT
 * /me/preferences/last-instance` once per change when it differs from the saved one: a switch in
 * the user menu, a pick on the picker page, a deep link, and the landing's fallback (so a gone
 * instance is replaced). Restoring the tab's choice on a reload and adopting the saved one send
 * nothing. Fire and forget: a failed write is not shown.
 *
 * **A change of instance** (a switch, a link into another instance, Back) resets what the screen
 * holds before the new instance renders: `switching` is true for the frame between, `App.tsx`
 * renders nothing of the shell in it, and a layout effect cancels in-flight queries and removes
 * every cached query except `/me` (the switch contract, client/AGENTS.md).
 */

/** Where an instance is saved for the person's next sign-in (round 4, 2026-09-30). */
const LAST_INSTANCE_PATH = "/api/v2/me/preferences/last-instance";

interface TenantInstanceContextValue {
  /** Undefined until `/api/v2/me` resolves. */
  me: MeResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  instances: MeInstance[];
  selectedInstance: MeInstance | null;
  /**
   * The instance the address names (`/i/<id>/...`) and whether this person can open it, or null
   * outside an instance's address. `checking` while `/me` is asked once more about an id it did
   * not list; `refused` for an id that is malformed, unknown, disabled or forbidden (never told
   * apart).
   */
  addressInstance: { id: string; status: "ok" | "checking" | "refused" } | null;
  /** The instance this tab was last in, other than the one the address names: a way back. */
  lastVisitedInstance: MeInstance | null;
  /**
   * True for the one frame between a change of instance and the reset of the cache; nothing that
   * reads instance data renders in it (`App.tsx`).
   */
  switching: boolean;
  /**
   * Switch to an instance. Inside an instance's address this navigates to the same section under
   * `/i/<new id>/` (`landingAfterInstanceSwitch` trims a record to its list), which the leave guard
   * can hold; on the picker or a platform screen it only changes the selection. `keepLocation` is
   * for a caller that names the record it is going to open in the new instance (a run link's
   * "Switch"): the screen's address is then kept, not trimmed. `replace` replaces the history
   * entry (the landing).
   */
  selectInstance: (
    instanceId: string,
    options?: { keepLocation?: boolean; replace?: boolean },
  ) => void;
  /**
   * Forgets the selection (the tab's and the browser's record). Inside an instance's address it
   * also asks `/me` again, which decides whether the address can still be opened.
   */
  clearInstance: () => void;
  /**
   * The instance a link names in its `?instance=` parameter (a link sent before round 9 T-58: a
   * notification's run link, contract-notif R12, D9), or null. Outside an instance's address
   * `App.tsx` sends such a link to `/i/<id>/...` first; inside one the address's instance wins and
   * this is never selected here, so the screen that reads it offers "Switch".
   */
  pendingInstanceId: string | null;
}

/** The `instance` parameter of a search string when it is a well-formed id, else null. */
export function pendingInstanceFrom(search: string): string | null {
  const value = new URLSearchParams(search).get("instance");
  return value && isInstanceIdShape(value) ? value.toLowerCase() : null;
}

/** The instance whose cache the page holds (`TenantInstanceProvider`): one value and its listeners. */
function createAppliedStore() {
  let value: string | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next: string) => {
      value = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const TenantInstanceContext = createContext<TenantInstanceContextValue | undefined>(undefined);

/** Where the selected instance came from; only the first one a page resolves is asked. */
type SelectionSource = "tab" | "chosen" | "only" | "saved" | "browser" | "address";

interface Selection {
  instance: MeInstance;
  source: SelectionSource;
}

/** The person's saved instance as this page first heard it, per signed-in user. */
interface SavedAtSignIn {
  userId: string;
  instanceId: string | null;
}

export function TenantInstanceProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const isSignedIn = status === "signed-in";
  const { data: me, isLoading, error } = useMe(isSignedIn);
  // Read once, at mount: what this tab held before a reload, and what this browser held.
  const [restoredTabId] = useState<string | null>(readTabInstance);
  const [browserId] = useState<string | null>(readBrowserInstance);
  const [selectedId, setSelectedId] = useState<string | null>(restoredTabId);
  const search = useSearch();
  const pendingInstanceId = pendingInstanceFrom(search);
  // The provider sits outside every router, so this is the whole address, `/i/<id>` included.
  const [location, navigate] = useLocation();
  const { instanceId: addressId, inner } = splitInstancePath(location);
  const queryClient = useQueryClient();

  const instances = useMemo(() => me?.instances ?? [], [me]);

  // The saved instance is taken from the FIRST `/me` of this sign-in only. A later refetch (window
  // focus) can carry another tab's switch, and adopting it would move this tab under the person's
  // feet. Set during render, React's pattern for state derived from a changing input: the frame
  // `/me` resolves on already has it.
  const [savedAtSignIn, setSavedAtSignIn] = useState<SavedAtSignIn | null>(null);
  // What this tab and browser held belongs to whoever was signed in when the page loaded. Another
  // person signing in on the same page (the dev sign-in does not reload) starts from their own.
  const [heldHereCounts, setHeldHereCounts] = useState(true);
  if (me && savedAtSignIn?.userId !== me.user.id) {
    if (savedAtSignIn) {
      setHeldHereCounts(false);
      setSelectedId(null);
    }
    setSavedAtSignIn({ userId: me.user.id, instanceId: me.preferences?.lastInstanceId ?? null });
  }
  const savedId = me && savedAtSignIn?.userId === me.user.id ? savedAtSignIn.instanceId : null;
  const tabId = heldHereCounts ? restoredTabId : null;
  const browserFallbackId = heldHereCounts ? browserId : null;

  // The instance this tab was last in: what is selected outside an instance's address.
  const lastVisited = useMemo<Selection | null>(() => {
    const reachable = (id: string | null) =>
      id ? (instances.find((i) => i.id === id) ?? null) : null;
    const chosen = reachable(selectedId);
    if (chosen) {
      return { instance: chosen, source: selectedId === tabId ? "tab" : "chosen" };
    }
    // Exactly one instance means there is no choice to make. Derived here
    // rather than set by an effect, so a single-instance user never bounces
    // through the picker on the frame before the effect runs.
    // #419: not one still provisioning; its Overview cannot load yet, so the picker shows it.
    if (instances.length === 1 && instances[0]?.status === "active") {
      return { instance: instances[0], source: "only" };
    }
    // Derived for the same reason; the effect below then keeps it for this tab and browser.
    const saved = reachable(savedId);
    if (saved) return { instance: saved, source: "saved" };
    const browser = reachable(browserFallbackId);
    if (browser) return { instance: browser, source: "browser" };
    return null;
  }, [instances, selectedId, tabId, savedId, browserFallbackId]);

  // The address's instance, when `/me` lists it. Never trusted beyond that: the server re-checks.
  const addressed = addressId ? (instances.find((i) => i.id === addressId) ?? null) : null;

  // An id `/me` does not list may be an instance created since `/me` was read: ask once more
  // before saying no. A malformed id is refused without asking.
  const [askedAbout, setAskedAbout] = useState<string | null>(null);
  const mustAsk =
    addressId !== null &&
    isInstanceIdShape(addressId) &&
    me !== undefined &&
    addressed === null &&
    askedAbout !== addressId;
  useEffect(() => {
    if (!mustAsk || addressId === null) return undefined;
    let live = true;
    void queryClient
      .refetchQueries({ queryKey: ME_QUERY_KEY, exact: true })
      .catch(() => undefined)
      .finally(() => {
        if (live) setAskedAbout(addressId);
      });
    return () => {
      live = false;
    };
  }, [mustAsk, addressId, queryClient]);

  const addressInstance = useMemo<TenantInstanceContextValue["addressInstance"]>(() => {
    if (addressId === null) return null;
    if (addressed) return { id: addressed.id, status: "ok" };
    if (me === undefined || mustAsk) return { id: addressId, status: "checking" };
    return { id: addressId, status: "refused" };
  }, [addressId, addressed, me, mustAsk]);

  const selection = useMemo<Selection | null>(() => {
    if (addressId === null) return lastVisited;
    if (!addressed) return null;
    return { instance: addressed, source: addressed.id === tabId ? "tab" : "address" };
  }, [addressId, addressed, lastVisited, tabId]);

  const selectedInstance = selection?.instance ?? null;
  const lastVisitedInstance =
    lastVisited && lastVisited.instance.id !== addressId ? lastVisited.instance : null;

  // While the address names an instance this person can open, it is the tab's choice too, so the
  // picker and a platform screen opened from here keep it. Set during render, like the saved id.
  if (addressed && selectedId !== addressed.id) setSelectedId(addressed.id);

  // Keep this tab's and this browser's record in step with what the user can actually reach.
  // Storage is the external system being synchronised, which is what an effect is for; the
  // selection itself stays DERIVED above.
  useEffect(() => {
    if (!me) return;
    if (selectedInstance) {
      keepInstanceHere(selectedInstance.id);
      return;
    }
    // An address this person cannot open leaves the record of where they were alone.
    if (addressId !== null) return;
    if (selectedId) {
      logger.info("stored instance is no longer available to this user");
      forgetTabInstance();
    }
    const held = readBrowserInstance();
    if (held && !instances.some((i) => i.id === held)) forgetBrowserInstance();
  }, [me, instances, selectedInstance, selectedId, addressId]);

  // Save the instance this page is in for the person's next sign-in, once per change (round 6
  // T-08). `held` is what the server holds as far as this page knows: on the first selection the
  // saved value, or the tab's own restored choice (a reload is not opening an instance, and
  // writing it would undo another tab's later switch); after that, the last id this page sent.
  // Never refreshed from a refetch, for the same reason. An older server whose `/me` has no
  // `preferences` has no route to write to.
  const held = useRef<{ userId: string; instanceId: string | null } | null>(null);
  useEffect(() => {
    if (!me?.preferences || !selection) return;
    const userId = me.user.id;
    const instanceId = selection.instance.id;
    if (held.current?.userId !== userId) {
      held.current = {
        userId,
        instanceId: selection.source === "tab" ? instanceId : (savedAtSignIn?.instanceId ?? null),
      };
    }
    if (held.current.instanceId === instanceId) return;
    held.current = { userId, instanceId };
    // Fire and forget: the switch is already made here; the write only decides where the next
    // sign-in lands, so a failure costs nothing on this screen and is not shown or retried.
    apiRequest(LAST_INSTANCE_PATH, { method: "PUT", body: { instanceId } }).catch(() => {
      logger.info("last instance was not remembered");
    });
  }, [me, selection, savedAtSignIn]);

  // A change of instance resets what the screen holds (the switch contract, client/AGENTS.md):
  // in-flight requests are cancelled and the cache goes, except the person's own `/me`, BEFORE the
  // new instance renders. A switch made here (`selectInstance`) resets in its own event, as
  // before. Any other change (Back, a link into another instance, the leave guard letting a held
  // switch go) is reset by the layout effect below: `switching` holds the frame between, in which
  // `App.tsx` renders none of the shell, so no observer of the old instance is left, and the reset
  // and the new id are committed before the browser paints. The shell is also keyed by the
  // instance id, so every page, draft and observer mounts afresh.
  const resetCache = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0] });
  }, [queryClient]);
  const targetId = selectedInstance?.id ?? null;
  // The last instance whose cache this page holds, kept outside React state: the layout effect
  // that resets the cache records it there, and the store re-renders this provider synchronously,
  // before the browser paints. The first selection, and an address with no instance to open,
  // change nothing that has to be reset.
  const [appliedStore] = useState(createAppliedStore);
  const applied = useSyncExternalStore(appliedStore.subscribe, appliedStore.get);
  const resetFor = useRef<string | null>(null);
  useLayoutEffect(() => {
    // A switch whose address was refused leaves nothing to skip later: its reset is forgotten.
    if (targetId === null) {
      resetFor.current = null;
      return;
    }
    if (applied === targetId) return;
    if (applied !== null && resetFor.current !== targetId) resetCache();
    resetFor.current = null;
    appliedStore.set(targetId);
  }, [applied, targetId, resetCache, appliedStore]);
  const switching = targetId !== null && applied !== null && applied !== targetId;

  // A switch made here is a navigation inside an instance's address: the same section under the
  // new id (a record trimmed to its list, a section the new instance does not allow to its first
  // allowed screen), so Back returns and the leave guard can hold it (B-09): a held switch changes
  // nothing until the reader answers, and Discard lets it go. The picker and a platform or
  // personal screen stay where they are; only the selection changes.
  const selectInstance = useCallback(
    (instanceId: string, options?: { keepLocation?: boolean; replace?: boolean }) => {
      beginUserAction();
      const current = selectedInstance?.id ?? null;
      if (addressId === null && isGlobalPath(inner)) {
        if (current !== null && current !== instanceId) {
          resetCache();
          resetFor.current = instanceId;
        }
        setSelectedId(instanceId);
        return;
      }
      if (addressId === instanceId) return;
      const next = me?.instances.find((i) => i.id === instanceId);
      const granted = new Set<string>([
        ...(next?.permissions ?? []),
        ...(me?.platformPermissions ?? []),
      ]);
      const target = options?.keepLocation
        ? inner
        : landingAfterInstanceSwitch(inner, (permission) => granted.has(permission));
      // The search goes with the old instance: a filter can name one of its records (the
      // Runs list's `dataflowId`), which the new instance does not hold.
      // A caller that keeps the location keeps it (a link's own record and parameters).
      const keptSearch = options?.keepLocation && search ? `?${search}` : "";
      const before = window.location.pathname;
      navigate(`${instancePath(instanceId, target)}${keptSearch}`, { replace: options?.replace });
      // Held by the leave guard: nothing changes until the reader answers.
      if (window.location.pathname === before) return;
      if (current !== null && current !== instanceId) {
        resetCache();
        resetFor.current = instanceId;
      }
      setSelectedId(instanceId);
    },
    [navigate, me, addressId, inner, search, selectedInstance, resetCache],
  );

  // Drops the tab's and the browser's choice. An address that names the instance still selects it
  // while `/me` lists it, so `/me` is asked again: an instance this person lost then shows the
  // in-shell "no access" state instead of answering every request with a refusal.
  const clearInstance = useCallback(() => {
    setSelectedId(null);
    forgetTabInstance();
    forgetBrowserInstance();
    if (addressId !== null) void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [addressId, queryClient]);

  // The fetch layer reads the instance through this getter, so a request fired
  // before React re-renders still carries the right header.
  // Publish the current instance to the API layer in a layout effect, not a passive effect:
  // React runs every layout effect (parents included) before any child's passive effect, and
  // TanStack Query starts its fetches from passive effects. With a passive effect here, a screen
  // that fired an instance-scoped query on the commit where /api/v2/me resolved sent its first
  // request with no x-instance-id and was refused with a 400.
  useLayoutEffect(() => {
    setInstanceGetter(() => selectedInstance?.id ?? null);
    return () => setInstanceGetter(null);
  }, [selectedInstance]);

  const value = useMemo<TenantInstanceContextValue>(
    () => ({
      me,
      isLoading: isSignedIn && isLoading,
      error: error as Error | null,
      instances,
      selectedInstance,
      addressInstance,
      lastVisitedInstance,
      switching,
      selectInstance,
      clearInstance,
      pendingInstanceId,
    }),
    [
      me,
      isSignedIn,
      isLoading,
      error,
      instances,
      selectedInstance,
      addressInstance,
      lastVisitedInstance,
      switching,
      selectInstance,
      clearInstance,
      pendingInstanceId,
    ],
  );

  return <TenantInstanceContext.Provider value={value}>{children}</TenantInstanceContext.Provider>;
}

export function useTenantInstance(): TenantInstanceContextValue {
  const context = useContext(TenantInstanceContext);
  if (!context) {
    throw new Error("useTenantInstance must be used within a TenantInstanceProvider");
  }
  return context;
}
